import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useOutletContext } from 'react-router-dom';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faRoute } from '@fortawesome/pro-regular-svg-icons/faRoute';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, type TableColumnsType } from 'antd';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import { PosPageContent } from 'pos-client/layouts/page/PosPageContent.tsx';
import {
  searchFilterByProperty,
  sortDates,
} from 'pos-client/utils/dataTableHelpers.tsx';
import { convertISOStringToLocalDate } from 'pos-client/utils/dateHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';
import type { RouteModel } from 'pos-shared/models/resourceManagementSchema.ts';

import { RoutesModal } from './components/modals/RoutesModal.tsx';

export const LocationRoutes = () => {
  const { t } = useTranslation();

  const { location } = useOutletContext<{
    location: LocationModel;
  }>();
  const {
    data: routes,
    isFetching: isRoutesLoading,
    refetch: refetchRoutes,
  } = trpc.getRoutes.useQuery(
    {
      locationId: location.id,
    },
    {
      enabled: !!location.id,
    }
  );

  const match = useMatch('/locations/:id/routes');
  const serviceLocationId = (match?.params.id || '') as string;

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [modalData, setModalData] = useState<RouteModel | undefined>(undefined);

  // eslint-disable-next-line
  const tableData = useTableData(routes?.results, (x: any) => x.id);
  const columns: TableColumnsType<RouteModel> = [
    {
      title: t('routes.routeName'),
      key: 'id',
      dataIndex: 'name',
      ...searchFilterByProperty<Pick<RouteModel, 'name'>>('name'),
    },
    {
      title: t('common.words.description'),
      key: 'description',
      dataIndex: 'description',
    },
    {
      title: t('common.audit.dateModified'),
      key: 'modifiedDateTime',
      dataIndex: 'modifiedDateTime',
      sorter: (a, b) =>
        sortDates<Pick<RouteModel, 'modifiedDateTime'>>(
          a,
          b,
          'modifiedDateTime'
        ),
      render: (column) => {
        return convertISOStringToLocalDate(column);
      },
    },
    {
      title: '',
      key: 'id',
      dataIndex: 'id',
      width: 80,
      align: 'center',
      render: (column) => (
        <Button size="small" onClick={() => editRoute(column)}>
          {t('common.buttons.edit')}
        </Button>
      ),
    },
  ];

  const handleOk = async () => {
    closeModal();
    refetchRoutes();
  };

  const handleCancel = () => {
    setModalData(undefined);
    closeModal();
  };

  const openModal = () => {
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setModalData(undefined);
  };

  const editRoute = (routeId: string) => {
    const route = tableData.find((item) => item.id === routeId);
    setModalData(route as RouteModel | undefined);
    setIsEditMode(true);
    openModal();
  };

  const addRoute = () => {
    setIsEditMode(false);
    setModalData(undefined);
    openModal();
  };

  return (
    <PosPageContent>
      <PosCard
        headerProps={{
          title: t('routes.routes'),
          icon: faRoute,
          action: (
            <Button
              onClick={() => addRoute()}
              icon={<FontAwesomeIcon icon={faPlus} />}
            >
              {t('common.buttons.addNew')}
            </Button>
          ),
        }}
      >
        <PosTable
          id="location-routes-table"
          dataSource={tableData}
          columns={columns}
          isLoading={isRoutesLoading}
          fePagination
        />

        {(modalData || !isEditMode) && (
          <RoutesModal
            data={modalData}
            isVisible={isModalVisible}
            isEditMode={isEditMode}
            serviceLocationId={serviceLocationId}
            handleOk={handleOk}
            closeModal={closeModal}
            handleCancel={handleCancel}
          />
        )}
      </PosCard>
    </PosPageContent>
  );
};
